/**
 * Transparent chapter-level storage for book-organized resources (scripture, TN, TQ).
 * The adapter uses this to split one logical entry into a manifest + one record per chapter,
 * and to reassemble on get, without changing the public get/set API.
 */
/** Key prefixes for book-organized resources that we store by chapter. */
export const BOOK_ORGANIZED_PREFIXES = ['scripture:', 'scripture-usj:', 'tn:', 'tq:'];
/** Marker in stored entry to indicate chunked (manifest-only) record. */
export const CHUNKED_MARKER = '_chunkedBook';
export function isBookOrganizedKey(key) {
    return BOOK_ORGANIZED_PREFIXES.some((p) => key.startsWith(p));
}
/**
 * Returns true if this key is a chapter sub-key (e.g. scripture:owner/lang/id:gen:1 or scripture:...:1:alignments).
 * Used to filter keys() to logical keys only.
 */
export function isChapterSubKey(key) {
    if (!isBookOrganizedKey(key))
        return false;
    const parts = key.split(':');
    const last = parts[parts.length - 1];
    const secondLast = parts.length > 2 ? parts[parts.length - 2] : '';
    return ((parts.length > 1 && /^\d+$/.test(last)) ||
        (parts.length > 2 && last === 'alignments' && /^\d+$/.test(secondLast)));
}
/**
 * Map a raw key to its logical key (base key for book-organized entries).
 * e.g. scripture:owner/lang/id:gen:1 -> scripture:owner/lang/id:gen
 *      scripture:owner/lang/id:gen:1:alignments -> scripture:owner/lang/id:gen
 */
export function toLogicalKey(key) {
    if (!isChapterSubKey(key))
        return key;
    if (key.endsWith(':alignments')) {
        const withoutAlignments = key.slice(0, -':alignments'.length);
        const lastColon = withoutAlignments.lastIndexOf(':');
        return lastColon > 0 ? withoutAlignments.slice(0, lastColon) : withoutAlignments;
    }
    const lastColon = key.lastIndexOf(':');
    return lastColon > 0 ? key.slice(0, lastColon) : key;
}
/**
 * Check if entry is a chunked manifest (has marker, no full content).
 */
export function isChunkedManifest(entry) {
    const e = entry;
    const meta = e?.metadata;
    const content = e?.content;
    return Boolean(meta?.[CHUNKED_MARKER] === true ||
        e?.[CHUNKED_MARKER] === true ||
        (content && typeof content === 'object' && content[CHUNKED_MARKER] === true));
}
// --- Scripture: entry.content.chapters (array) ---
function isScriptureEntry(entry) {
    const c = entry.content ?? entry;
    return Array.isArray(c?.chapters);
}
function scriptureChapters(entry) {
    const content = entry.content;
    const chapters = content.chapters ?? [];
    return { metadata: content.metadata, chapters, content };
}
export function canSplitScripture(key, entry) {
    const chapters = entry.content?.chapters;
    // Legacy ProcessedScripture only — not scripture-usj: (handled separately)
    return (key.startsWith('scripture:') &&
        !key.startsWith('scripture-usj:') &&
        isScriptureEntry(entry) &&
        (chapters?.length ?? 0) > 0);
}
/** USJ SoT: content.chapters = [{ number, content: nodes[] }], plus alignmentMap. */
function isUsjScriptureEntry(entry) {
    const c = entry.content ?? entry;
    const chapters = c?.chapters;
    if (!Array.isArray(chapters) || chapters.length === 0)
        return false;
    const first = chapters[0];
    // ProcessedScripture chapters have verses[]; USJ slices have content nodes[]
    return Array.isArray(first?.content) && !Array.isArray(first?.verses);
}
export function canSplitUsjScripture(key, entry) {
    return key.startsWith('scripture-usj:') && isUsjScriptureEntry(entry);
}
/** WordAlignment has verseRef e.g. "TIT 1:1"; we split by chapter using bookCode + " " + ch + ":". */
function alignmentsForChapter(alignments, bookCode, chapterNum) {
    if (!alignments?.length)
        return [];
    const prefix = `${bookCode} ${chapterNum}:`.toLowerCase();
    return alignments.filter((a) => (a.verseRef ?? '').toLowerCase().startsWith(prefix));
}
/**
 * Split scripture for chapter-level storage. Manifest has no chapters and no alignments (they are large).
 * Chapter content → key:chNum. Chapter alignments → key:chNum:alignments (separate entries).
 */
export function splitScriptureEntry(key, entry) {
    const { metadata, chapters, content } = scriptureChapters(entry);
    const alignments = content.alignments ?? [];
    const bookCodeFromKey = key.split(':').pop() ?? '';
    const bookCode = String((content.bookCode ?? content.metadata?.bookCode) ?? bookCodeFromKey).toLowerCase() || '';
    const bookLevelContent = {
        ...content,
        metadata,
        chapters: undefined,
        alignments: undefined,
        [CHUNKED_MARKER]: true,
    };
    delete bookLevelContent.chapters;
    delete bookLevelContent.alignments;
    const manifestEntry = {
        ...entry,
        content: bookLevelContent,
        metadata: { ...entry.metadata, [CHUNKED_MARKER]: true },
    };
    const chapterNums = chapters.map((ch, i) => ch?.number ?? i + 1);
    const chapterEntries = chapters.map((ch, i) => {
        const chapterNum = ch?.number ?? i + 1;
        return {
            key: `${key}:${chapterNum}`,
            entry: { ...entry, content: ch },
        };
    });
    const alignmentEntries = chapterNums.map((chapterNum) => ({
        key: `${key}:${chapterNum}:alignments`,
        entry: {
            ...entry,
            content: alignmentsForChapter(alignments, bookCode || 'unknown', chapterNum),
        },
    }));
    return { manifestEntry, chapterEntries, alignmentEntries };
}
/**
 * Split USJ SoT for chapter-level storage.
 * Chapter nodes → key:chNum. AlignmentMap subset → key:chNum:alignments.
 */
export function splitUsjScriptureEntry(key, entry) {
    const content = entry.content;
    const chapters = content.chapters ?? [];
    const alignmentMap = content.alignmentMap ?? {};
    const bookCode = String(content.bookCode ?? content.metadata?.bookCode ?? key.split(':').pop() ?? '').toLowerCase();
    const bookLevelContent = {
        ...content,
        chapters: undefined,
        usj: undefined,
        alignmentMap: undefined,
        [CHUNKED_MARKER]: true,
    };
    delete bookLevelContent.chapters;
    delete bookLevelContent.usj;
    delete bookLevelContent.alignmentMap;
    const manifestEntry = {
        ...entry,
        content: bookLevelContent,
        metadata: { ...entry.metadata, [CHUNKED_MARKER]: true },
    };
    const chapterEntries = chapters.map((ch, i) => {
        const chapterNum = ch?.number ?? i + 1;
        return {
            key: `${key}:${chapterNum}`,
            entry: { ...entry, content: { number: chapterNum, content: ch.content ?? [] } },
        };
    });
    const chapterNums = chapters.map((ch, i) => ch?.number ?? i + 1);
    const alignmentEntries = chapterNums.map((chapterNum) => {
        const prefix = `${bookCode} ${chapterNum}:`.toLowerCase();
        const subset = {};
        for (const [verseRef, groups] of Object.entries(alignmentMap)) {
            if (verseRef.toLowerCase().startsWith(prefix)) {
                subset[verseRef] = groups;
            }
        }
        return {
            key: `${key}:${chapterNum}:alignments`,
            entry: { ...entry, content: subset },
        };
    });
    return { manifestEntry, chapterEntries, alignmentEntries };
}
export function reassembleUsjScripture(manifestEntry, chapterRecords) {
    const content = manifestEntry.content;
    const contentEntries = chapterRecords.filter((r) => !r.key.endsWith(':alignments'));
    const alignmentEntries = chapterRecords.filter((r) => r.key.endsWith(':alignments'));
    const chapters = contentEntries
        .sort((a, b) => {
        const na = parseInt(a.key.split(':').pop() ?? '0', 10);
        const nb = parseInt(b.key.split(':').pop() ?? '0', 10);
        return na - nb;
    })
        .map((c) => {
        const ch = c.entry.content;
        return { number: ch.number ?? 0, content: ch.content ?? [] };
    });
    const alignmentMap = {};
    for (const r of alignmentEntries) {
        const subset = r.entry.content;
        if (subset && typeof subset === 'object' && !Array.isArray(subset)) {
            Object.assign(alignmentMap, subset);
        }
    }
    const usjContent = [];
    for (const ch of chapters) {
        usjContent.push(...(ch.content ?? []));
    }
    const reassembledContent = {
        ...content,
        chapters,
        alignmentMap,
        usj: { type: 'USJ', version: '3.0', content: usjContent },
    };
    delete reassembledContent[CHUNKED_MARKER];
    const outMetadata = manifestEntry.metadata
        ? (() => {
            const m = { ...manifestEntry.metadata };
            delete m[CHUNKED_MARKER];
            return m;
        })()
        : undefined;
    return {
        ...manifestEntry,
        content: reassembledContent,
        metadata: outMetadata,
    };
}
/**
 * All records under this book key (content key:chNum and alignment key:chNum:alignments).
 * We separate into content entries and alignment entries, then reassemble.
 */
export function reassembleScripture(manifestEntry, chapterRecords) {
    const content = manifestEntry.content;
    const metadata = content?.metadata;
    const contentEntries = chapterRecords.filter((r) => !r.key.endsWith(':alignments'));
    const alignmentEntries = chapterRecords.filter((r) => r.key.endsWith(':alignments'));
    const chapters = contentEntries
        .sort((a, b) => {
        const na = parseInt(a.key.split(':').pop() ?? '0', 10);
        const nb = parseInt(b.key.split(':').pop() ?? '0', 10);
        return na - nb;
    })
        .map((c) => c.entry.content);
    const alignments = alignmentEntries
        .sort((a, b) => {
        const aParts = a.key.split(':');
        const bParts = b.key.split(':');
        const na = parseInt(aParts[aParts.length - 2] ?? '0', 10);
        const nb = parseInt(bParts[bParts.length - 2] ?? '0', 10);
        return na - nb;
    })
        .flatMap((r) => (Array.isArray(r.entry.content) ? r.entry.content : []));
    const reassembledContent = { ...content, metadata, chapters, alignments: alignments.length > 0 ? alignments : undefined };
    delete reassembledContent[CHUNKED_MARKER];
    const outMetadata = manifestEntry.metadata
        ? (() => {
            const m = { ...manifestEntry.metadata };
            delete m[CHUNKED_MARKER];
            return m;
        })()
        : undefined;
    return {
        ...manifestEntry,
        content: reassembledContent,
        metadata: outMetadata,
    };
}
// --- TN: entry.notesByChapter (Record<string, TranslationNote[]>) ---
function isNotesEntry(entry) {
    const e = entry;
    return typeof e?.notesByChapter === 'object' && e.notesByChapter !== null && !Array.isArray(e.notesByChapter);
}
function notesByChapter(entry) {
    const e = entry;
    return e.notesByChapter ?? {};
}
export function canSplitNotes(key, entry) {
    return key.startsWith('tn:') && isNotesEntry(entry) && Object.keys(notesByChapter(entry)).length > 0;
}
export function splitNotesEntry(key, entry) {
    const byChapter = notesByChapter(entry);
    const entryAny = entry;
    const rest = Object.fromEntries(Object.entries(entryAny).filter(([k]) => k !== 'notesByChapter' && k !== 'notes'));
    const meta = entryAny.metadata && typeof entryAny.metadata === 'object' ? entryAny.metadata : {};
    const manifestEntry = {
        content: {},
        ...rest,
        notesByChapter: {},
        notes: [],
        metadata: { ...meta, [CHUNKED_MARKER]: true },
        [CHUNKED_MARKER]: true,
    };
    const chapterEntries = Object.entries(byChapter).map(([chapterNum, notes]) => ({
        key: `${key}:${chapterNum}`,
        entry: { content: { notes }, metadata: entryAny.metadata },
    }));
    return { manifestEntry, chapterEntries };
}
export function reassembleNotes(manifestEntry, chapterEntries) {
    const base = manifestEntry;
    const out = {};
    let notes = [];
    for (const { key: chKey, entry: chEntry } of chapterEntries) {
        const chapterNum = chKey.split(':').pop() ?? '0';
        const chAny = chEntry;
        const chContent = chAny.content;
        const arr = chContent?.notes ?? chAny.notes ?? [];
        out[chapterNum] = Array.isArray(arr) ? arr : [];
        notes = notes.concat(out[chapterNum]);
    }
    const restored = { ...base, notesByChapter: out, notes };
    delete restored[CHUNKED_MARKER];
    if (restored.metadata && typeof restored.metadata === 'object')
        delete restored.metadata[CHUNKED_MARKER];
    return restored;
}
// --- TQ: entry.questionsByChapter ---
function isQuestionsEntry(entry) {
    const e = entry;
    return typeof e?.questionsByChapter === 'object' && e.questionsByChapter !== null && !Array.isArray(e.questionsByChapter);
}
function questionsByChapter(entry) {
    const e = entry;
    return e.questionsByChapter ?? {};
}
export function canSplitQuestions(key, entry) {
    return key.startsWith('tq:') && isQuestionsEntry(entry) && Object.keys(questionsByChapter(entry)).length > 0;
}
export function splitQuestionsEntry(key, entry) {
    const byChapter = questionsByChapter(entry);
    const entryAny = entry;
    const rest = Object.fromEntries(Object.entries(entryAny).filter(([k]) => k !== 'questionsByChapter' && k !== 'questions'));
    const meta = entryAny.metadata && typeof entryAny.metadata === 'object' ? entryAny.metadata : {};
    const manifestEntry = {
        content: {},
        ...rest,
        questionsByChapter: {},
        questions: [],
        metadata: { ...meta, [CHUNKED_MARKER]: true },
        [CHUNKED_MARKER]: true,
    };
    const chapterEntries = Object.entries(byChapter).map(([chapterNum, questions]) => ({
        key: `${key}:${chapterNum}`,
        entry: { content: { questions }, metadata: entryAny.metadata },
    }));
    return { manifestEntry, chapterEntries };
}
export function reassembleQuestions(manifestEntry, chapterEntries) {
    const base = manifestEntry;
    const out = {};
    let questions = [];
    for (const { key: chKey, entry: chEntry } of chapterEntries) {
        const chapterNum = chKey.split(':').pop() ?? '0';
        const chAny = chEntry;
        const chContent = chAny.content;
        const arr = chContent?.questions ?? chAny.questions ?? [];
        out[chapterNum] = Array.isArray(arr) ? arr : [];
        questions = questions.concat(out[chapterNum]);
    }
    const restored = { ...base, questionsByChapter: out, questions };
    delete restored[CHUNKED_MARKER];
    if (restored.metadata && typeof restored.metadata === 'object')
        delete restored.metadata[CHUNKED_MARKER];
    return restored;
}
// --- Unified split / reassemble ---
export function canSplitBookEntry(key, entry) {
    return (canSplitUsjScripture(key, entry) ||
        canSplitScripture(key, entry) ||
        canSplitNotes(key, entry) ||
        canSplitQuestions(key, entry));
}
export function splitBookEntry(key, entry) {
    if (canSplitUsjScripture(key, entry))
        return splitUsjScriptureEntry(key, entry);
    if (canSplitScripture(key, entry))
        return splitScriptureEntry(key, entry);
    if (canSplitNotes(key, entry))
        return splitNotesEntry(key, entry);
    if (canSplitQuestions(key, entry))
        return splitQuestionsEntry(key, entry);
    return { manifestEntry: entry, chapterEntries: [] };
}
/**
 * Reassemble from manifest + all chapter-related records (content key:ch and, for scripture, alignment key:ch:alignments).
 */
export function reassembleBookEntry(key, manifestEntry, chapterRecords) {
    if (key.startsWith('scripture-usj:'))
        return reassembleUsjScripture(manifestEntry, chapterRecords);
    if (key.startsWith('scripture:'))
        return reassembleScripture(manifestEntry, chapterRecords);
    if (key.startsWith('tn:'))
        return reassembleNotes(manifestEntry, chapterRecords);
    if (key.startsWith('tq:'))
        return reassembleQuestions(manifestEntry, chapterRecords);
    return manifestEntry;
}
